import React, { useState, useEffect, useCallback } from 'react';
import { FiPlus, FiTrash2, FiX, FiTrendingUp, FiBarChart2, FiZap, FiFolder } from 'react-icons/fi';
import { getAuthHeaders } from '../utils/auth';
import { getApiEndpoint } from '../utils/api';
import './Enterprise.css';

const Enterprise = ({ onBack, accountId }) => {
  const [account, setAccount] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberRole, setNewMemberRole] = useState('member');
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  const fetchAccount = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(getApiEndpoint(`/api/enterprise/accounts/${accountId}`), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAccount(data);
      } else {
        setError('Failed to load enterprise account');
      }
    } catch (err) {
      setError('Failed to load enterprise account');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  const fetchUsage = useCallback(async () => {
    if (!accountId) return;
    try {
      const response = await fetch(getApiEndpoint(`/api/enterprise/accounts/${accountId}/usage`), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setUsage(data);
      }
    } catch (err) {
      console.error('Failed to fetch usage:', err);
    }
  }, [accountId]);

  const fetchUsageForAccount = useCallback(async (id) => {
    try {
      const response = await fetch(getApiEndpoint(`/api/enterprise/accounts/${id}/usage`), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setUsage(data);
      }
    } catch (err) {
      console.error('Failed to fetch usage:', err);
    }
  }, []);

  const fetchUserEnterpriseAccounts = useCallback(async () => {
    try {
      const response = await fetch(getApiEndpoint('/api/enterprise/accounts'), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        if (data.length > 0) {
          // Use the first enterprise account
          setAccount(data[0]);
          fetchUsageForAccount(data[0].id);
        }
      }
    } catch (err) {
      console.error('Failed to fetch enterprise accounts:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchUsageForAccount]);

  useEffect(() => {
    fetchCurrentUser();
    if (accountId) {
      fetchAccount();
      fetchUsage();
    } else {
      // If no accountId provided, try to get the first enterprise account
      fetchUserEnterpriseAccounts();
    }
  }, [accountId, fetchAccount, fetchUsage, fetchUserEnterpriseAccounts]);

  const fetchCurrentUser = async () => {
    try {
      const response = await fetch(getApiEndpoint('/api/auth/me'), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentUser(data);
      }
    } catch (err) {
      console.error('Failed to fetch current user:', err);
    }
  };


  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!newMemberEmail.trim()) {
      alert('Please enter an email address');
      return;
    }

    try {
      const response = await fetch(getApiEndpoint(`/api/enterprise/accounts/${account.id}/members`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          email: newMemberEmail.trim(),
          role: newMemberRole
        })
      });

      if (response.ok) {
        setShowAddMemberModal(false);
        setNewMemberEmail('');
        setNewMemberRole('member');
        await fetchAccount();
      } else {
        const errorData = await response.json();
        alert(errorData.detail || 'Failed to add member');
      }
    } catch (err) {
      alert('Failed to add member');
      console.error(err);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to remove this member?')) {
      return;
    }

    try {
      const response = await fetch(getApiEndpoint(`/api/enterprise/accounts/${account.id}/members/${memberId}`), {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        await fetchAccount();
      } else {
        const errorData = await response.json();
        alert(errorData.detail || 'Failed to remove member');
      }
    } catch (err) {
      alert('Failed to remove member');
      console.error(err);
    }
  };

  const handleUpdateSeats = async (memberCount) => {
    try {
      const response = await fetch(getApiEndpoint(`/api/enterprise/accounts/${account.id}/seats`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          member_count: memberCount
        })
      });

      if (response.ok) {
        const data = await response.json();
        alert(data.message || `Updated seats to ${memberCount}`);
        await fetchAccount();
      } else {
        const errorData = await response.json();
        alert(errorData.detail || 'Failed to update seats');
      }
    } catch (err) {
      alert('Failed to update seats');
      console.error(err);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your Enterprise subscription? You will retain access until the end of your billing period.')) {
      return;
    }

    try {
      const response = await fetch(getApiEndpoint(`/api/enterprise/accounts/${account.id}/cancel`), {
        method: 'POST',
        headers: getAuthHeaders()
      });

      const data = await response.json();

      if (response.ok) {
        alert(data.message || 'Subscription will be canceled at the end of the billing period');
        await fetchAccount();
      } else {
        alert(data.detail || 'Failed to cancel subscription');
        console.error('Cancel subscription error:', data);
      }
    } catch (err) {
      alert('Failed to cancel subscription: ' + err.message);
      console.error('Cancel subscription exception:', err);
    }
  };

  const handleResumeSubscription = async () => {
    try {
      const response = await fetch(getApiEndpoint(`/api/enterprise/accounts/${account.id}/resume`), {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        alert('Subscription resumed');
        await fetchAccount();
      } else {
        const errorData = await response.json();
        alert(errorData.detail || 'Failed to resume subscription');
      }
    } catch (err) {
      alert('Failed to resume subscription');
      console.error(err);
    }
  };

  const isOwner = account && currentUser && account.owner_id === currentUser.id;

  if (loading) {
    return (
      <div className="enterprise-container">
        <div className="loading">Loading enterprise account...</div>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="enterprise-container">
        {onBack && (
          <button className="back-button" onClick={onBack}>
            ← Back
          </button>
        )}
        <div className="no-account">
          <h2>No Enterprise Account</h2>
          <p>You don't have access to any enterprise accounts.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="enterprise-container ph-no-capture">
      {onBack && (
        <button className="back-button" onClick={onBack}>
          ← Back
        </button>
      )}

      {error && (
        <div className="error-message">{error}</div>
      )}

      <div className="enterprise-header">
        <div>
          <h1>{account.name}</h1>
          <p className="enterprise-subtitle">Enterprise Account</p>
        </div>
        <div className="account-status">
          <span className={`status-badge ${account.status}`}>
            {account.status}
          </span>
        </div>
      </div>

      {/* Account Overview */}
      <div className="enterprise-section">
        <div className="section-header">
          <h2>Account Overview</h2>
          {isOwner && (
            <div className="subscription-actions">
              {account.cancel_at_period_end ? (
                <button
                  className="resume-button"
                  onClick={handleResumeSubscription}
                  disabled={!account.stripe_subscription_id}
                >
                  Resume Subscription
                </button>
              ) : (
                <button
                  className="cancel-button"
                  onClick={handleCancelSubscription}
                >
                  Cancel Subscription
                </button>
              )}
            </div>
          )}
        </div>
        <div className="overview-grid">
          <div className="overview-card">
            <div className="overview-label">Plan</div>
            <div className="overview-value">{account.plan.name}</div>
          </div>
          <div className="overview-card">
            <div className="overview-label">Members</div>
            <div className="overview-value">
              {account.current_member_count} / {account.purchased_member_count}
            </div>
            {isOwner && account.stripe_subscription_id && (
              <button
                className="update-seats-button"
                onClick={() => {
                  const newCount = prompt(`Current seats: ${account.purchased_member_count}\nEnter new number of seats (minimum: ${account.current_member_count}):`);
                  if (newCount) {
                    const count = parseInt(newCount);
                    if (!isNaN(count) && count >= account.current_member_count) {
                      handleUpdateSeats(count);
                    } else {
                      alert(`Please enter a valid number of seats (minimum: ${account.current_member_count})`);
                    }
                  }
                }}
                style={{ marginTop: '0.5rem', fontSize: '0.875rem', padding: '0.5rem 1rem' }}
              >
                Update Seats
              </button>
            )}
          </div>
          <div className="overview-card">
            <div className="overview-label">Billing Cycle</div>
            <div className="overview-value">{account.billing_cycle}</div>
          </div>
          <div className="overview-card">
            <div className="overview-label">Owner</div>
            <div className="overview-value">{account.owner_username}</div>
          </div>
        </div>
        {account.cancel_at_period_end && (
          <div className="cancel-notice" style={{ marginTop: '1rem', padding: '1rem', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px' }}>
            <p style={{ margin: 0, color: '#856404' }}>
              ⚠️ Your subscription will be canceled at the end of the billing period ({new Date(account.current_period_end).toLocaleDateString()})
            </p>
          </div>
        )}
      </div>

      {/* Usage Dashboard */}
      {usage && (
        <div className="enterprise-section">
          <h2>Usage This Month</h2>
          <div className="usage-grid">
            <div className="usage-card">
              <div className="usage-header">
                <FiBarChart2 />
                <span>Searches</span>
              </div>
              <div className="usage-value">
                {usage.limits?.searches_per_month 
                  ? `${usage.searches_count} / ${usage.limits.searches_per_month}`
                  : `${usage.searches_count} (Unlimited)`
                }
              </div>
            </div>

            <div className="usage-card">
              <div className="usage-header">
                <FiFolder />
                <span>Collections</span>
              </div>
              <div className="usage-value">
                {usage.limits?.collections
                  ? `${usage.collections_created} / ${usage.limits.collections}`
                  : `${usage.collections_created} (Unlimited)`
                }
              </div>
            </div>

            <div className="usage-card">
              <div className="usage-header">
                <FiZap />
                <span>Chatbot Tokens</span>
              </div>
              <div className="usage-value">
                {usage.limits?.chatbot_tokens_per_month
                  ? `${usage.chatbot_tokens_used.toLocaleString()} / ${usage.limits.chatbot_tokens_per_month.toLocaleString()}`
                  : `${usage.chatbot_tokens_used.toLocaleString()} (Unlimited)`
                }
              </div>
            </div>

            <div className="usage-card">
              <div className="usage-header">
                <FiTrendingUp />
                <span>Storage</span>
              </div>
              <div className="usage-value">
                {usage.limits?.storage_gb
                  ? `${usage.storage_gb.toFixed(2)} GB / ${usage.limits.storage_gb} GB`
                  : `${usage.storage_gb.toFixed(2)} GB (Unlimited)`
                }
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Member Management */}
      {isOwner && (
        <div className="enterprise-section">
          <div className="section-header">
            <h2>Members</h2>
            {account.current_member_count < account.purchased_member_count && (
              <button
                className="add-member-button"
                onClick={() => setShowAddMemberModal(true)}
              >
                <FiPlus />
                Add Member
              </button>
            )}
          </div>

          <div className="members-list">
            {account.members.map(member => (
              <div key={member.id} className="member-card">
                <div className="member-info">
                  <div>
                    <div className="member-name">{member.username}</div>
                    <div className="member-email">{member.email}</div>
                  </div>
                  <div className="member-role-badge">{member.role}</div>
                </div>
                {member.role !== 'owner' && (
                  <button
                    className="remove-member-button"
                    onClick={() => handleRemoveMember(member.id)}
                    title="Remove member"
                  >
                    <FiTrash2 />
                  </button>
                )}
              </div>
            ))}
          </div>

          {account.current_member_count >= account.purchased_member_count && (
            <div className="member-limit-warning">
              Member limit reached ({account.current_member_count}/{account.purchased_member_count}). 
              Upgrade to add more members.
            </div>
          )}
        </div>
      )}

      {/* Member View (Non-Owner) */}
      {!isOwner && (
        <div className="enterprise-section">
          <h2>Members</h2>
          <div className="members-list">
            {account.members.map(member => (
              <div key={member.id} className="member-card">
                <div className="member-info">
                  <div>
                    <div className="member-name">{member.username}</div>
                    <div className="member-email">{member.email}</div>
                  </div>
                  <div className="member-role-badge">{member.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {showAddMemberModal && (
        <div className="modal-overlay" onClick={() => setShowAddMemberModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Member</h3>
              <button className="modal-close" onClick={() => setShowAddMemberModal(false)}>
                <FiX />
              </button>
            </div>
            <form onSubmit={handleAddMember}>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={newMemberEmail}
                  onChange={(e) => setNewMemberEmail(e.target.value)}
                  placeholder="user@example.com"
                  required
                />
              </div>
              <div className="form-group">
                <label>Role</label>
                <select
                  value={newMemberRole}
                  onChange={(e) => setNewMemberRole(e.target.value)}
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowAddMemberModal(false)}>
                  Cancel
                </button>
                <button type="submit">Add Member</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Enterprise;

